import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import { NodeSelection, TextSelection } from '@atlaskit/editor-prosemirror/state';
import { DecorationSet } from '@atlaskit/editor-prosemirror/view';
import { fg } from '@atlaskit/platform-feature-flags';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';
import { selectionPluginKey } from '../types';
import { SelectionActionTypes } from './actions';
import { onCreateSelectionBetween } from './events/create-selection-between';
import { createOnKeydown } from './events/keydown';
import { onMouseOut } from './events/mouseout';
import { createPluginState, getPluginState } from './plugin-factory';
import { getDecorations, shouldRecalcDecorations } from './utils';
export var getInitialState = function getInitialState(state) {
  return {
    decorationSet: getDecorations(state.tr),
    selection: state.selection
  };
};
export var createPlugin = function createPlugin(api, dispatch, dispatchAnalyticsEvent) {
  var options = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
  var cursorHidden = false;
  return new SafePlugin({
    key: selectionPluginKey,
    state: createPluginState(dispatch, getInitialState),
    // @ts-ignore - Workaround for help-center local consumption
    appendTransaction: function appendTransaction(transactions, oldEditorState, newEditorState) {
      var tr = newEditorState.tr;
      var manualSelection;
      var hideCursorChanged = false;
      var needsHideCursor = fg('platform_editor_ai_aifc_patch_beta');
      var needsManualSelection = editorExperiment('platform_editor_element_drag_and_drop_multiselect', true);
      if (needsHideCursor || needsManualSelection) {
        for (var i = transactions.length - 1; i >= 0; i--) {
          var meta = transactions[i].getMeta(selectionPluginKey);
          if (needsHideCursor && (meta === null || meta === void 0 ? void 0 : meta.hideCursor) !== undefined) {
            var newHideCursorValue = meta.hideCursor;
            if (cursorHidden !== newHideCursorValue) {
              cursorHidden = newHideCursorValue;
              hideCursorChanged = true;
            }
          }
          if (needsManualSelection && meta !== null && meta !== void 0 && meta.manualSelection && !manualSelection) {
            manualSelection = meta.manualSelection;
            if (!needsHideCursor) {
              break;
            }
          }
        }
      }
      if (!shouldRecalcDecorations({
        oldEditorState: oldEditorState,
        newEditorState: newEditorState
      }) && !manualSelection && !hideCursorChanged) {
        return;
      }
      tr.setMeta(selectionPluginKey, {
        type: SelectionActionTypes.SET_DECORATIONS,
        selection: tr.selection,
        decorationSet: getDecorations(tr, manualSelection, cursorHidden)
      });
      return tr;
    },
    // @ts-ignore - Workaround for help-center local consumption
    filterTransaction: function filterTransaction(tr, state) {
      // Prevent single click selecting atom nodes on mobile (we want to select with long press gesture instead)
      if (options.useLongPressSelection && tr.selectionSet && tr.selection instanceof NodeSelection && !tr.getMeta(selectionPluginKey)) {
        return false;
      }

      // Prevent prosemirror's mutation observer overriding a node selection with a text selection
      // for exact same range - this was cause of being unable to change dates in collab:
      // https://product-fabric.atlassian.net/browse/ED-10645
      if (state.selection instanceof NodeSelection && tr.selection instanceof TextSelection && state.selection.from === tr.selection.from && state.selection.to === tr.selection.to) {
        return false;
      }
      return true;
    },
    props: {
      // @ts-ignore - Workaround for help-center local consumption

      createSelectionBetween: onCreateSelectionBetween,
      // @ts-ignore - Workaround for help-center local consumption
      decorations: function decorations(state) {
        var _api$interaction;
        var interactionState = api === null || api === void 0 || (_api$interaction = api.interaction) === null || _api$interaction === void 0 || (_api$interaction = _api$interaction.sharedState.currentState()) === null || _api$interaction === void 0 ? void 0 : _api$interaction.interactionState;

        // Do not show selection decorations for live pages where the user has not
        // interacted with the page. We do not show cursor until interaction and we do not
        // want to show selections either.
        if ((options.__livePage || expValEquals('platform_editor_no_cursor_on_edit_page_init', 'isEnabled', true)) && interactionState === 'hasNotHadInteraction') {
          return DecorationSet.empty;
        }
        return getPluginState(state).decorationSet;
      },
      // @ts-ignore - Workaround for help-center local consumption

      handleDOMEvents: {
        // @ts-ignore - Workaround for help-center local consumption

        keydown: createOnKeydown({
          __livePage: options.__livePage
        }),
        // Without this event, it is not possible to click and drag to select the first node in the
        // document if the node is a block with content (e.g. a panel with a paragraph inside).
        mouseout: onMouseOut
      }
    }
  });
};